/**
 * Game mode arena, Round 2 (plan 47): the pieces the thread renders around a
 * translate-it challenge.
 *
 * - {@link GameChallengeHead} — the head of the challenge block INSIDE the
 *   coach bubble («Ronda N», «Revancha» / «2º intento», and «No sé» on the
 *   challenge on the table). The block itself is drawn by `SpokenBody` (the
 *   sentence keeps its karaoke and tap-a-word) or by `CoachCard` when the
 *   sentence cannot be found in the text.
 * - {@link GameRevealCard} — the quiet card a «No sé» leaves in the thread:
 *   «Una forma de decirlo: …», stamped «Vista».
 * - {@link GameAttemptSolutionPanel} / {@link GameSolutionButton} — «Ver
 *   solución» under a «Casi» / «No era eso» attempt: the 1–2 versions
 *   («Más natural», «Cerca de lo que dijiste»), its loading and failed states.
 */
import type { CSSProperties, ReactElement } from 'react';
import { directionForSeed } from '../controls/directionsData.js';
import type { GameChallengeTag } from '../gameChallenge.js';
import type { GameSolutionView, RevealCardModel } from '../gameSolutions.js';
import type { GameDontKnowControl } from '../hooks/useGameArena.js';
import '../gameArena.css';

/** Learner-facing copy (rioplatense Spanish, like the rest of speak). */
export const GAME_ROUND2_COPY = {
  round: 'Ronda',
  retry: 'Revancha',
  secondShot: '2º intento',
  blockAria: (round: number) => `Ronda ${round}: decila en inglés`,
  dontKnow: 'No sé',
  dontKnowAria: 'No sé cómo se dice: mostrame una forma',
  dontKnowUsed: 'Vista',
  seen: 'Vista',
  seenAria: 'Frase vista: no cuenta como intento',
  revealLead: 'Una forma de decirlo',
  revealLoading: 'Buscando una forma de decirlo',
  failed: 'No pudimos traer la solución',
  retryFetch: 'Reintentar',
  showSolution: 'Ver solución',
  showSolutionAria: 'Ver la solución de esta frase',
  solutionLoading: 'Buscando la solución',
  natural: 'Más natural',
  closest: 'Cerca de lo que dijiste',
  concealed: 'tocá para ver',
  concealedAria: 'Mostrar la solución de este intento',
} as const;

/** translate-it's own colour (the block, like the pill, wears the game's hue). */
export function translateItColorStyle(): CSSProperties | undefined {
  const color = directionForSeed('translate-it')?.color;
  return color ? ({ '--dir-color': color } as CSSProperties) : undefined;
}

/** «Revancha» (the learner's ↺) / «2º intento» (the coach's second shot), or nothing. */
export function challengeBadgeOf(
  tag: Pick<GameChallengeTag, 'rematch' | 'attemptNo'>,
): string | null {
  if (tag.rematch === 'retry') return GAME_ROUND2_COPY.retry;
  if (tag.rematch === 'second-shot' || tag.attemptNo > 1) return GAME_ROUND2_COPY.secondShot;
  return null;
}

interface GameChallengeHeadProps {
  tag: GameChallengeTag;
  /** «No sé» — only on the challenge on the table. */
  dontKnow?: GameDontKnowControl | undefined;
  onDontKnow?: (() => void) | undefined;
}

/**
 * The head row of the challenge block: «RONDA N» + badge on the left, the
 * quiet «No sé» on the right. Spans and a button only — it lives inside the
 * bubble's inline text body.
 */
export function GameChallengeHead({
  tag,
  dontKnow,
  onDontKnow,
}: GameChallengeHeadProps): ReactElement {
  const badge = challengeBadgeOf(tag);
  return (
    <span className="game-cblock__head">
      <span className="game-cblock__kicker">
        {GAME_ROUND2_COPY.round} {tag.round}
      </span>
      {badge && <span className="game-cblock__badge">{badge}</span>}
      {dontKnow && onDontKnow && (
        <button
          type="button"
          className={`game-cblock__dontknow${dontKnow.used ? ' is-used' : ''}`}
          disabled={dontKnow.disabled}
          onClick={(e) => {
            // The block sits inside the tappable text: a press is not a word tap.
            e.stopPropagation();
            onDontKnow();
          }}
          aria-label={GAME_ROUND2_COPY.dontKnowAria}
        >
          {dontKnow.used ? GAME_ROUND2_COPY.dontKnowUsed : GAME_ROUND2_COPY.dontKnow}
        </button>
      )}
    </span>
  );
}

/** The «Vista» stamp: neutral grey, an eye + text (never colour alone). */
export function GameSeenStamp(): ReactElement {
  return (
    <span className="game-stamp is-seen" role="img" aria-label={GAME_ROUND2_COPY.seenAria}>
      <EyeIcon />
      <span className="game-stamp__text" aria-hidden="true">
        {GAME_ROUND2_COPY.seen}
      </span>
    </span>
  );
}

export function EyeIcon({ size = 14 }: { size?: number }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="game-stamp__icon"
    >
      <path d="M2.5 12 C 5.5 6.5, 18.5 6.5, 21.5 12 C 18.5 17.5, 5.5 17.5, 2.5 12 Z" />
      <circle cx="12" cy="12" r="2.6" />
    </svg>
  );
}

function Dots(): ReactElement {
  return (
    <span className="conv-typing-dots game-solution__dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

/**
 * The card a «No sé» leaves in the thread, right after the challenge it
 * revealed (live: the last message when it was pressed; history: after turn
 * `afterTurnIndex`). Quiet: no colour of its own, the «Vista» stamp says what
 * happened. The coach never says the translation — this card is the only
 * place it shows.
 */
export function GameRevealCard({ card }: { card: RevealCardModel }): ReactElement {
  const { view } = card;
  return (
    <section
      className="game-reveal slide-up"
      aria-label={`${GAME_ROUND2_COPY.seen}: ${card.challenge}`}
    >
      <div className="game-reveal__top">
        <GameSeenStamp />
        <span className="game-reveal__challenge" lang="es">
          {card.challenge}
        </span>
      </div>
      <div className="game-reveal__body" role="status" aria-live="polite">
        {view.status === 'ready' ? (
          <>
            <span className="game-solution__label">{GAME_ROUND2_COPY.revealLead}:</span>{' '}
            <span className="game-solution__text" lang="en">
              {view.solutions[0]}
            </span>
          </>
        ) : view.status === 'loading' ? (
          <span className="game-solution__muted">
            {GAME_ROUND2_COPY.revealLoading}
            <Dots />
          </span>
        ) : (
          <span className="game-solution__muted">{GAME_ROUND2_COPY.failed}</span>
        )}
      </div>
    </section>
  );
}

interface GameAttemptSolutionPanelProps {
  view: GameSolutionView;
  /** A rematch of this attempt is pending: the solution is blurred until tapped. */
  concealed?: boolean;
  onRevealConcealed?: (() => void) | undefined;
  /** «Reintentar» after a failure; absent = no retry (history). */
  onRetry?: (() => void) | undefined;
  retryDisabled?: boolean;
}

/**
 * «Ver solución» answered, under the attempt bubble: the most natural
 * version, and the one closest to the learner's wording when the server sent
 * a second. Loading and failed states in place (the button above it hides
 * once asked).
 */
export function GameAttemptSolutionPanel({
  view,
  concealed = false,
  onRevealConcealed,
  onRetry,
  retryDisabled = false,
}: GameAttemptSolutionPanelProps): ReactElement | null {
  if (view.status === 'refused') return null;
  if (concealed && view.status === 'ready') {
    return (
      <button
        type="button"
        className="game-solution is-concealed"
        onClick={onRevealConcealed}
        aria-label={GAME_ROUND2_COPY.concealedAria}
      >
        <span className="game-solution__blur" aria-hidden="true">
          {GAME_ROUND2_COPY.natural}: {view.solutions[0]}
        </span>
        <span className="game-conceal__label">{GAME_ROUND2_COPY.concealed}</span>
      </button>
    );
  }
  return (
    <div className="game-solution" role="status" aria-live="polite">
      {view.status === 'ready' ? (
        view.solutions.map((s, i) => (
          <div key={i} className="game-solution__line">
            <span className="game-solution__label">
              {i === 0 ? GAME_ROUND2_COPY.natural : GAME_ROUND2_COPY.closest}:
            </span>{' '}
            <span className="game-solution__text" lang="en">
              {s}
            </span>
          </div>
        ))
      ) : view.status === 'loading' ? (
        <span className="game-solution__muted">
          {GAME_ROUND2_COPY.solutionLoading}
          <Dots />
        </span>
      ) : (
        <span className="game-solution__failed">
          <span className="game-solution__muted">{GAME_ROUND2_COPY.failed}</span>
          {onRetry && (
            <button
              type="button"
              className="game-solution__retry"
              onClick={onRetry}
              disabled={retryDisabled}
            >
              {GAME_ROUND2_COPY.retryFetch}
            </button>
          )}
        </span>
      )}
    </div>
  );
}

/** The subtle «Ver solución» ghost button of a «Casi» / «No era eso» attempt. */
export function GameSolutionButton({
  onAsk,
  disabled,
}: {
  onAsk: () => void;
  disabled: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      className="game-attempt-action is-solution"
      disabled={disabled}
      onClick={onAsk}
      aria-label={GAME_ROUND2_COPY.showSolutionAria}
    >
      {GAME_ROUND2_COPY.showSolution}
    </button>
  );
}
